import React, { useEffect, useState } from "react";
import Link from "next/link";
import Meta from "../Meta";
import PageHeader from "../components/PageHeader";
import Icon from "../components/Icon";
import EmptyState from "../components/EmptyState";
import Loader from "../loader";
import ApiService from "../../../services/ApiService";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { resolveImage } from "../utils";

const styles = `
.t26-actu-page {
  padding: 0 clamp(16px, 4vw, 32px) clamp(40px, 6vw, 80px);
  max-width: var(--container);
  margin-inline: auto;
}
.t26-actu-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: clamp(16px, 2.5vw, 24px);
  grid-auto-rows: 1fr;
  align-items: stretch;
}
.t26-actu-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  overflow: hidden;
  display: flex; flex-direction: column;
  height: 100%;
  transition: transform .2s, box-shadow .2s, border-color .2s;
  text-decoration: none;
  color: inherit;
}
.t26-actu-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-md);
  border-color: color-mix(in oklab, var(--primary) 35%, var(--line));
}
.t26-actu-card__media {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  background: color-mix(in oklab, var(--primary) 14%, var(--bg-2));
  overflow: hidden;
  flex-shrink: 0;
}
.t26-actu-card__media img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  display: block;
}
.t26-actu-card__body {
  padding: 18px;
  display: flex; flex-direction: column; gap: 10px;
  flex: 1;
}
.t26-actu-card__tag {
  font-size: 10px; font-weight: 700;
  letter-spacing: .18em; text-transform: uppercase;
  color: var(--secondary);
}
.t26-actu-card__title {
  font-family: var(--font-display);
  font-size: 18px; font-weight: 700;
  line-height: 1.25;
  color: var(--ink);
  margin: 0;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.t26-actu-card__date { font-size: 12px; color: var(--ink-3); margin-top: auto; }
`;

export default function Actu({ context, loading }) {
    const { org, langValue } = context;
    const [data, setData] = useState([]);
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        if (!org?.id) { setBusy(false); return; }
        let cancelled = false;
        (async () => {
            try {
                // /actualite/{orgId} = vraies actus de l'org (pas /auto-content qui sert pour
                // les articles externes type Loubavitch et a un format différent)
                const res = await ApiService.get("/actualite/" + org.id + "?page=1");
                if (!cancelled && Array.isArray(res?.data)) setData(res.data);
            } catch (err) {
                // silencieux
            } finally {
                if (!cancelled) setBusy(false);
            }
        })();
        return () => { cancelled = true; };
    }, [org?.id, langValue]);

    if (loading || busy) {
        return (
            <>
                <Meta context={context} title={t`Actualités`} description={t`Actualités de ${org.name}`} />
                <Loader />
            </>
        );
    }

    return (
        <>
            <Meta context={context} title={t`Actualités`} description={t`Actualités de ${org.name}`} />
            <style dangerouslySetInnerHTML={{ __html: styles }} />

            <PageHeader
                title={t`Actualités`}
                subtitle={t`Suivez nos dernières publications et inspirations`}
                crumbs={[
                    { label: t`Accueil`, url: org.BASE_WEB_URL },
                    { label: t`Actualités` },
                ]}
            />

            <div className="t26-actu-page">
                {data.length === 0 ? (
                    <EmptyState
                        icon="IconNews"
                        title={t`Aucune actualité pour le moment`}
                        subtitle={t`Les prochaines publications apparaîtront ici très bientôt.`}
                    />
                ) : (
                    <div className="t26-actu-grid">
                        {data.map(item => {
                            // Type LINK = lien externe (item.link), TEXT/IMAGE = page interne /actualite/{id}
                            const isExternal = item.type === "LINK" && item.link;
                            const url = isExternal
                                ? item.link
                                : `${org.BASE_WEB_URL}/actualite/${item.id}`;
                            const cover = Array.isArray(item.images) && item.images.length > 0 ? item.images[0] : null;
                            const tag = Array.isArray(item.thematiques) && item.thematiques.length > 0 ? item.thematiques[0] : null;
                            const inner = (
                                <div className="t26-actu-card">
                                    <div className="t26-actu-card__media">
                                        {cover && <img src={resolveImage(cover)} alt={item.title || ""} loading="lazy" />}
                                    </div>
                                    <div className="t26-actu-card__body">
                                        {tag && <span className="t26-actu-card__tag">{tag}</span>}
                                        <h3 className="t26-actu-card__title">{item.title}</h3>
                                        {item.datetime && (
                                            <span className="t26-actu-card__date">
                                                {new Date(item.datetime).toLocaleDateString(langValue || "fr")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                            if (isExternal) {
                                return <a key={item.id} href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>{inner}</a>;
                            }
                            return <Link key={item.id} href={url} legacyBehavior><a style={{ textDecoration: "none" }}>{inner}</a></Link>;
                        })}
                    </div>
                )}
            </div>
        </>
    );
}
